'use client';

import { memo } from 'react';

import { Link as NextLink } from '../../routing/routing';
import { Modify } from '../../types/DocumentRefType';
import { Link as LinkModel } from '../../types/schemas/objects/Link';
import { SanityObject } from '../../types/schemas/types/SanityObject';
import { cn } from '../../utils/css/cn';
import { isInternal } from '../../utils/url/isInternal';
import { normalizeHref } from '../../utils/url/normalizeHref';
import { Icon } from '../icon/Icon';

export interface LinkProps
	extends Modify<
		React.ComponentProps<typeof NextLink>,
		Omit<LinkModel, keyof SanityObject<'Link'>>
	> {
	children?: React.ReactNode;
	iconSize?: string;
	iconClassName?: string;
}

const Link = memo<LinkProps>(
	({
		href = '',
		label,
		altText,
		target,
		children,
		role,
		passHref,
		showIcon,
		icon,
		iconPosition,
		iconSize,
		iconClassName,
		...other
	}) => {
		// @ts-expect-error
		const handleKeyDown = (e) => {
			if (role === 'button' && e.keyCode == 32) {
				e.preventDefault();
				e.target?.click();
			}
		};

		// @ts-expect-error
		const handleClick = (e) => {
			if (href.startsWith('#')) {
				e.preventDefault();
				const element = document.getElementById(href.replace('#', ''));
				if (element) {
					const headerHeight =
						document.querySelector<HTMLElement>('.sb-page > header')?.offsetHeight ?? 0;
					window.scroll({ top: element.offsetTop - headerHeight, behavior: 'smooth' });
				}
			}
		};

		if (isInternal(href)) {
			return (
				<NextLink
					href={href}
					passHref={passHref}
					target={target}
					onKeyDown={handleKeyDown}
					onClick={handleClick}
					role={role}
					aria-label={label || altText}
					{...other}
				>
					{showIcon && icon && iconPosition === 'left' && (
						<Icon size={iconSize} name={icon} className={cn('mr-2', iconClassName)} />
					)}
					{label && <span data-testid="label">{label}</span>}
					{showIcon && icon && iconPosition === 'right' && (
						<Icon size={iconSize} name={icon} className={cn('ml-2', iconClassName)} />
					)}
					{children}
				</NextLink>
			);
		}

		return (
			<a
				href={normalizeHref(href)}
				{...other}
				target={target ?? '_blank'}
				rel="noreferrer"
				onKeyDown={handleKeyDown}
				role={role}
				aria-label={label || altText}
			>
				{showIcon && icon && iconPosition === 'left' && (
					<Icon size={iconSize} name={icon} className={cn('mr-2', iconClassName)} />
				)}
				{label && <span data-testid="label">{label}</span>}
				{showIcon && icon && iconPosition === 'right' && (
					<Icon size={iconSize} name={icon} className={cn('ml-2', iconClassName)} />
				)}
				{children}
			</a>
		);
	},
);

Link.displayName = 'Link';

export default Link;
